import { Component, type ErrorInfo, type ReactNode } from 'react';
import { faro } from '@grafana/faro-web-sdk';

/**
 * Catches render-time crashes anywhere below it and shows the error instead of
 * a blank page.
 *
 * Without a boundary, React unmounts the whole tree on an uncaught render
 * error, which presents as a white screen with nothing in the UI to act on —
 * the failure mode we hit on the sketch route. Lazy-loaded routes make it worse,
 * because a failed chunk fetch looks identical to a crash.
 *
 * The reported detail is deliberately verbatim (message, stack, component
 * stack) and copyable, since that is what makes a production white-page
 * reproducible.
 */

interface Props {
  children: ReactNode;
  /** Shown in the heading so nested boundaries can identify themselves. */
  label?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    // Keep the raw objects in the console — the on-screen copy is a summary.
    console.error('[ErrorBoundary] render crash', error, info.componentStack);

    // Ship it to Faro explicitly. Nothing else does:
    //   - Faro's error instrumentation hooks window.onerror / unhandledrejection,
    //     and catching the error here is precisely what stops it reaching those.
    //   - captureConsole is false in faro.ts, so the console.error above goes
    //     nowhere either.
    // Without this call a render crash is invisible in production. The sketch-page
    // crash in INC-2026-002 was reported by a user, not by monitoring, and the
    // alert on frontend errors has nothing to fire on until this signal exists.
    try {
      faro?.api?.pushError(error, {
        type: 'react-render-crash',
        context: {
          boundary: this.props.label ?? 'root',
          url: window.location.href,
          // Bounded: component stacks on a deep tree can be very large, and the
          // transport batches by item count, not bytes.
          componentStack: (info.componentStack ?? '').slice(0, 4000),
        },
      });
    } catch {
      // Telemetry must never be the reason the error screen fails to render.
    }
  }

  private report(): string {
    const { error, componentStack } = this.state;
    return [
      `Error: ${error?.name}: ${error?.message}`,
      `URL: ${window.location.href}`,
      `UA: ${navigator.userAgent}`,
      '',
      '--- stack ---',
      error?.stack ?? '(none)',
      '',
      '--- component stack ---',
      componentStack ?? '(none)',
    ].join('\n');
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          padding: '32px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 13,
          lineHeight: 1.6,
          backgroundColor: 'var(--restore-dark-bg, #0d1117)',
          color: 'var(--restore-text-primary, #e6edf3)',
          overflowY: 'auto',
        }}
      >
        <h1 style={{ fontSize: 18, marginBottom: 4, color: '#f85149' }}>
          {this.props.label ? `${this.props.label} crashed` : 'Something crashed'}
        </h1>
        <p style={{ marginTop: 0, color: 'var(--restore-text-muted, #8b949e)' }}>
          This screen replaces what used to be a blank page. Copy the details below.
        </p>

        <div style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
          <button
            onClick={() => navigator.clipboard?.writeText(this.report())}
            style={btn('#238636')}
          >
            Copy error details
          </button>
          <button onClick={() => window.location.reload()} style={btn('#30363d')}>
            Reload
          </button>
          <button
            onClick={() => this.setState({ error: null, componentStack: null })}
            style={btn('#30363d')}
          >
            Try again
          </button>
        </div>

        <Section title="Error">
          {error.name}: {error.message}
        </Section>
        <Section title="Stack">{error.stack ?? '(none)'}</Section>
        <Section title="Component stack">{componentStack ?? '(none)'}</Section>
      </div>
    );
  }
}

function btn(bg: string): React.CSSProperties {
  return {
    background: bg,
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    font: 'inherit',
  };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ color: '#8b949e', marginBottom: 4 }}>{title}</div>
      <pre
        style={{
          margin: 0,
          padding: 12,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </pre>
    </div>
  );
}

export default ErrorBoundary;
